import { useTranslation, Trans } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/lilia-ui/dialog";
import { Button } from "@/components/lilia-ui/button";
import { Spinner } from "@/components/lilia-ui/spinner";
import { useSharingApi } from "@/hooks/useSharingApi";
import { useDocument } from "@/hooks/useDocument";
import type { UserCollaborator } from "@/lib/api";
import { getVisibilityState, visibilityStrip } from "@/lib/sharing/visibilityStrip";
import { InvitePeopleSection } from "./sections/InvitePeopleSection";
import { TeamAccessSection } from "./sections/TeamAccessSection";
import { PublicLinkSection } from "./sections/PublicLinkSection";

interface ShareDrawerProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  /** Doc title — used in the drawer heading. Optional; falls back
   *  to the document fetched by id if not provided. */
  documentTitle?: string;
  /** Initial public-link state from the caller (avoids a flash
   *  while we fetch). The drawer takes over after first fetch. */
  isPublic?: boolean;
  shareLink?: string;
  shareSlug?: string;
  /** Doc owner — needed so the people list can render the owner
   *  row with its badge instead of a removable selector. */
  ownerId?: string;
}

// Spec §4 — the unified drawer. Right-side drawer on desktop
// (≥ lg), bottom-sheet via fullscreen overrides on mobile. Width
// 460 px desktop (not full-screen). Three stacked sections (NOT
// tabs — spec hard rule #1). Header carries the doc title and the
// visibility strip; footer carries the "Changes save
// automatically" hint and Done button.
export function ShareDrawer({
  open,
  onClose,
  documentId,
  documentTitle,
  isPublic: initialIsPublic,
  shareLink: initialShareLink,
  shareSlug: initialShareSlug,
  ownerId,
}: ShareDrawerProps) {
  const { t } = useTranslation("sharing");
  const sharingApi = useSharingApi();
  const queryClient = useQueryClient();
  const { document: doc } = useDocument(documentId);

  // Mirror the caller's initial public-link state, but let user
  // actions inside the drawer be the source of truth after open.
  const [isPublic, setIsPublic] = useState(initialIsPublic ?? false);
  const [shareLink, setShareLink] = useState(initialShareLink ?? "");
  const [shareSlug, setShareSlug] = useState(initialShareSlug ?? "");
  // Iter 8 — track persisted permission + expiry. Pulled from the
  // doc on first load; PublicLinkSection persists changes and
  // pushes them back here via onChange.
  const [linkPermission, setLinkPermission] = useState<string>("view");
  const [linkExpiresAt, setLinkExpiresAt] = useState<string | null>(null);
  const [collaborators, setCollaborators] = useState<UserCollaborator[]>([]);
  // The spinner is for the first load only: the list looks again every few
  // seconds while open (someone may join), and must stay in place meanwhile.
  const [loaded, setLoaded] = useState(false);

  const fetchCollaborators = useCallback(async () => {
    try {
      const r = await sharingApi.listCollaborators(documentId);
      setCollaborators(r.users);
    } finally {
      setLoaded(true);
    }
  }, [documentId, sharingApi]);

  useEffect(() => {
    if (!open) return;
    setIsPublic(initialIsPublic ?? doc?.isPublic ?? false);
    setShareLink(initialShareLink ?? doc?.shareLink ?? "");
    setShareSlug(initialShareSlug ?? doc?.shareSlug ?? "");
    // doc may carry the new fields once the iter-8 API ships.
    // Falling back to defaults keeps the section sane during the
    // brief window where the editor lands before the API does.
    setLinkPermission((doc as { linkPermission?: string } | undefined)?.linkPermission ?? "view");
    setLinkExpiresAt((doc as { linkExpiresAt?: string | null } | undefined)?.linkExpiresAt ?? null);
    void fetchCollaborators();
  }, [open, initialIsPublic, initialShareLink, initialShareSlug, doc, fetchCollaborators]);

  const title = documentTitle ?? doc?.title ?? t("drawer.thisDocument");
  const teamId = doc?.teamId ?? null;
  const teamName = doc?.teamName ?? null;
  const resolvedOwnerId = ownerId ?? doc?.ownerId;

  const visibility = useMemo(
    () =>
      getVisibilityState({
        collaborators,
        ownerId: resolvedOwnerId,
        teamId,
        teamName,
        isPublic,
      }),
    [collaborators, resolvedOwnerId, teamId, teamName, isPublic],
  );

  const stripText = useMemo(
    () =>
      visibilityStrip({
        collaborators,
        ownerId: resolvedOwnerId,
        teamId,
        teamName,
        isPublic,
      }),
    [collaborators, resolvedOwnerId, teamId, teamName, isPublic],
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* Right-side drawer (lg+); fullscreen sheet on mobile. Width
          460 px from spec §4. The custom className overrides the
          centered-modal defaults of the shared DialogContent. */}
      <DialogContent
        data-testid="share-drawer"
        className="!left-auto !right-0 !top-0 !translate-x-0 !translate-y-0 !h-[100dvh] !max-h-[100dvh] !w-full !max-w-[460px] !rounded-l-xl !rounded-r-none !p-0 max-lg:!max-w-none"
      >
        <DialogHeader className="!border-b-0 !pb-0 px-5 pt-5">
          <DialogTitle className="font-serif text-xl font-bold leading-tight tracking-tight">
            <Trans t={t} i18nKey="drawer.title" values={{ title }} components={{ em: <em className="not-italic" /> }} />
          </DialogTitle>
          <DialogDescription className="mt-1 text-xs text-[var(--lilia-text-muted)]">
            {t("drawer.youOwn")}
          </DialogDescription>

          {/* Visibility strip — §4 point 1. Single source for
              header + topbar tooltip + mobile Panels subtitle. */}
          <div className="mt-3 flex items-center gap-2 rounded-md bg-[var(--lilia-bg)] px-3 py-2 text-xs text-[var(--lilia-text)]">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                visibility.solo ? "bg-[var(--lilia-text-muted)]" : "bg-emerald-500"
              }`}
              aria-hidden
            />
            <span>{stripText}</span>
          </div>
        </DialogHeader>

        {/* Scrollable body — sections stack vertically. */}
        <div className="flex-1 overflow-y-auto px-5 pb-2 pt-1">
          {!loaded ? (
            <div className="flex items-center justify-center py-10">
              <Spinner />
            </div>
          ) : (
            <>
              <InvitePeopleSection
                documentId={documentId}
                ownerId={resolvedOwnerId}
                collaborators={collaborators}
                onChange={fetchCollaborators}
              />
              <TeamAccessSection
                documentId={documentId}
                attachedTeamId={teamId}
                attachedTeamName={teamName}
                collaborators={collaborators}
                ownerId={resolvedOwnerId}
                onChange={() => {
                  void fetchCollaborators();
                  // Attach / detach changes the document's team: without this the section kept
                  // offering "Attach to …" after attaching, until the page was reloaded.
                  void queryClient.invalidateQueries({ queryKey: ["document", documentId] });
                }}
              />
              <PublicLinkSection
                documentId={documentId}
                isPublic={isPublic}
                shareLink={shareLink}
                shareSlug={shareSlug}
                linkPermission={linkPermission}
                linkExpiresAt={linkExpiresAt}
                onChange={(s) => {
                  // The caller's props come from the cached document, so the
                  // cache has to learn the change too. Without this, reopening
                  // the drawer showed a live link as off.
                  queryClient.setQueryData(["document", documentId], (d: object | undefined) =>
                    d && {
                      ...d,
                      isPublic: s.isPublic,
                      shareLink: s.shareLink,
                      shareSlug: s.shareSlug ?? "",
                      ...(s.linkPermission ? { linkPermission: s.linkPermission } : {}),
                      ...(s.linkExpiresAt !== undefined ? { linkExpiresAt: s.linkExpiresAt } : {}),
                    });
                  void queryClient.invalidateQueries({ queryKey: ["documents"] });
                  setIsPublic(s.isPublic);
                  setShareLink(s.shareLink);
                  setShareSlug(s.shareSlug ?? "");
                  if (s.linkPermission) setLinkPermission(s.linkPermission);
                  if (s.linkExpiresAt !== undefined) setLinkExpiresAt(s.linkExpiresAt);
                }}
              />
            </>
          )}
        </div>

        <footer className="flex items-center gap-3 border-t border-[var(--lilia-border)] px-5 py-3">
          <p className="flex-1 text-[11px] text-[var(--lilia-text-muted)]">
            {t("drawer.autosave")}
          </p>
          <Button data-testid="share-drawer-done" variant="primary" size="sm" onClick={onClose}>
            {t("drawer.done")}
          </Button>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
